import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import useStore from '../../store'
import { weeksLived, weeksLeft, fmtYearsMo } from '../../lib/timeline'
import { timeOverage } from '../../lib/constraints'

export default function ReEntry({ onDismiss }) {
  const navigate    = useNavigate()
  const profile     = useStore(s => s.profile)
  const goals       = useStore(s => s.goals)

  if (!profile) return null

  const now        = new Date()
  const last       = profile.last_session_at ? new Date(profile.last_session_at) : null
  const daysSince  = last ? Math.floor((now - last) / 86400000) : 0
  const weeksSince = Math.floor(daysSince / 7)

  const left    = weeksLeft(profile.date_of_birth, profile.expected_lifespan)
  const active  = goals.filter(g => g.status === 'active')
  const overage = timeOverage(active, profile.weekly_available_hours)

  const counts = {
    active:  active.length,
    paused:  goals.filter(g => g.status === 'paused').length,
    backlog: goals.filter(g => g.bucket === 'aspirational' && !['completed','archived'].includes(g.status)).length,
  }

  return (
    <motion.div
      className="min-h-screen bg-m-bg flex items-center justify-center p-6"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
    >
      <div className="w-full max-w-md flex flex-col gap-5">

        {/* Wordmark */}
        <div>
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-m-accent">MORI</span>
          <span className="font-mono text-xs text-m-subtle ml-3">what remains is yours</span>
        </div>

        <div className="divider" />

        {/* Welcome back */}
        <div>
          <h1 className="font-mono text-2xl text-m-text">Welcome back.</h1>
          <p className="font-mono text-sm text-m-subtle mt-1">
            {weeksSince > 0
              ? `${weeksSince} ${weeksSince === 1 ? 'week' : 'weeks'} since your last session.`
              : `${daysSince} days since your last session.`}
          </p>
        </div>

        {/* Time consumed */}
        <div className="card p-4 grid grid-cols-2 gap-4">
          <div>
            <p className="label">Weeks consumed</p>
            <p className="font-mono text-2xl text-m-text mt-1">{weeksSince}</p>
          </div>
          <div>
            <p className="label">Remaining lifetime</p>
            <p className="font-mono text-2xl text-m-accent mt-1">{fmtYearsMo(left)}</p>
          </div>
        </div>

        {/* Goal status */}
        <div className="card p-4">
          <p className="label mb-3">Current plan</p>
          <div className="grid grid-cols-3 gap-3">
            {[['Active', counts.active], ['Paused', counts.paused], ['Backlog', counts.backlog]].map(([l, v]) => (
              <div key={l}>
                <p className="label">{l}</p>
                <p className="font-mono text-xl text-m-text mt-0.5">{v}</p>
              </div>
            ))}
          </div>

          {overage > 0 && (
            <div className="alert-warn mt-3">
              Current plan exceeds capacity by {overage.toFixed(1)} hrs/week.
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button onClick={onDismiss} className="btn-primary flex-1">
            Continue
          </button>
          <button
            onClick={() => { navigate('/review'); onDismiss() }}
            className="btn-ghost flex-1"
          >
            Review & Realign
          </button>
        </div>

        <p className="font-mono text-[10px] text-m-faint text-center">
          what remains is yours to allocate deliberately
        </p>
      </div>
    </motion.div>
  )
}
